import {
  BufferGeometry,
  Float32BufferAttribute,
  Group,
  Mesh,
} from "three";

import { ROAD_MARKING_RENDER_CONFIG } from "../../config/roadMarkings.ts";
import type { StreetSurfaceFamily } from "../../config/streets.ts";
import { pointInPolygon } from "../../world/geometry.ts";
import type { LocalHorizontal } from "../../world/coordinates.ts";
import type {
  RuntimeAreaFeature,
  RuntimeLinearFeature,
  RuntimeWorldTilePayloadV2,
} from "../../world/schema/runtimeTile.ts";
import type { TerrainTileData } from "../../world/schema/tile.ts";
import {
  isPedestrianStreetFeature,
  streetFeatureSurfaceElevationM,
  streetFeatureWidthM,
} from "../../world/streets.ts";
import type { CityMaterialLibrary } from "../materials/cityMaterials.ts";

export type LongitudinalRoadMarkingKind =
  | "centre"
  | "lane-divider"
  | "edge"
  | "bus-lane"
  | "cycle-lane"
  | "parking-bay"
  | "stop-line"
  | "turn-arrow";

export type RoadMarkingLineStyle = "dashed" | "solid";

export interface RoadMarkingRibbonSegment {
  readonly featureId: string;
  readonly kind: LongitudinalRoadMarkingKind;
  readonly from: LocalHorizontal;
  readonly to: LocalHorizontal;
  readonly fromTangent: LocalHorizontal;
  readonly toTangent: LocalHorizontal;
  readonly offsetM: number;
  readonly widthM: number;
  readonly style: RoadMarkingLineStyle;
}

export interface RoadMarkingBatch {
  readonly object: Group;
  readonly markedFeatureCount: number;
  readonly centreDashCount: number;
  readonly laneDividerDashCount: number;
  readonly ribbonSegmentCount: number;
}

interface PolylineSample {
  readonly point: LocalHorizontal;
  readonly tangent: LocalHorizontal;
}

interface EndpointRecord {
  readonly featureId: string;
  readonly name: string | null;
  readonly outward: LocalHorizontal;
}

interface MutableMeshData {
  readonly positions: number[];
  readonly uvs: number[];
  readonly indices: number[];
}

const ENDPOINT_QUANTIZATION_M = 0.2;

export function createRoadMarkingBatch(
  tile: Readonly<RuntimeWorldTilePayloadV2>,
  terrain: Readonly<TerrainTileData>,
  family: StreetSurfaceFamily,
  materials: CityMaterialLibrary,
): RoadMarkingBatch | null {
  const segments = roadMarkingRibbonSegmentsForTile(tile, family);
  if (segments.length === 0) {
    return null;
  }
  const meshData: MutableMeshData = {
    positions: [],
    uvs: [],
    indices: [],
  };
  for (const segment of segments) {
    appendRibbonSegment(meshData, segment, tile, terrain);
  }
  if (meshData.indices.length === 0) {
    return null;
  }
  const geometry = new BufferGeometry();
  geometry.setAttribute(
    "position",
    new Float32BufferAttribute(meshData.positions, 3),
  );
  geometry.setAttribute("uv", new Float32BufferAttribute(meshData.uvs, 2));
  geometry.setIndex(meshData.indices);
  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  geometry.name = `runtime-road-markings:${tile.id}:geometry`;

  const object = new Group();
  object.name = `runtime-road-markings:${tile.id}`;
  object.userData = {
    tileId: tile.id,
    sourceLayer: "official-grpk-road-centrelines-and-widths",
    sourceLicense: "CC-BY-4.0",
    placement:
      "paved-carriageways-with-two-or-more-standard-width-traffic-lanes",
    representation:
      "terrain-following-mapped-lanes-solid-dashed-edge-control-and-arrow-paint",
    narrowStreetPolicy: "unmarked-below-six-metres",
  };
  const mesh = new Mesh(geometry, materials.laneMarking);
  mesh.name = `${object.name}:longitudinal-paint`;
  mesh.castShadow = false;
  mesh.receiveShadow = true;
  mesh.renderOrder = 4;
  object.add(mesh);

  return {
    object,
    markedFeatureCount: new Set(segments.map((segment) => segment.featureId))
      .size,
    centreDashCount: countDashRuns(segments, "centre"),
    laneDividerDashCount: countDashRuns(segments, "lane-divider"),
    ribbonSegmentCount: segments.length,
  };
}

export function roadMarkingRibbonSegmentsForTile(
  tile: Readonly<RuntimeWorldTilePayloadV2>,
  family: StreetSurfaceFamily,
): readonly Readonly<RoadMarkingRibbonSegment>[] {
  const roads = tile.transportLines.filter(
    (feature) => markingOffsetsForFeature(feature, family).length > 0,
  );
  const junctionEndpointKeys = findJunctionEndpointKeys(
    tile.transportLines.filter((feature) =>
      feature.kind === "road" && !isPedestrianStreetFeature(feature)
    ),
  );
  const crossingAreas = crossingExclusionAreas(tile.hardscapeAreas ?? []);
  const longitudinal = roads.flatMap((feature) => {
    const totalLengthM = polylineLengthM(feature.points);
    if (totalLengthM <= 0.5) {
      return [];
    }
    const startKey = endpointKey(feature.points[0]);
    const endKey = endpointKey(feature.points.at(-1));
    const startIsJunction = startKey !== null &&
      junctionEndpointKeys.has(startKey);
    const endIsJunction = endKey !== null &&
      junctionEndpointKeys.has(endKey);
    const maximumTrimM = Math.max(0, totalLengthM / 2 - 0.12);
    const trimStartM = startIsJunction
      ? Math.min(ROAD_MARKING_RENDER_CONFIG.junctionTrimM, maximumTrimM)
      : 0;
    const trimEndM = endIsJunction
      ? Math.min(ROAD_MARKING_RENDER_CONFIG.junctionTrimM, maximumTrimM)
      : 0;
    if (trimStartM + trimEndM >= totalLengthM - 0.2) {
      return [];
    }
    return markingOffsetsForFeature(feature, family).flatMap((marking) =>
      patternedSegments(
        feature,
        marking.kind,
        marking.offsetM,
        marking.widthM,
        marking.style,
        trimStartM,
        totalLengthM - trimEndM,
        tile.origin,
        crossingAreas,
      )
    );
  });
  return [
    ...longitudinal,
    ...controlLineSegments(tile),
    ...turnArrowSegments(tile),
  ];
}

function markingOffsetsForFeature(
  feature: Readonly<RuntimeLinearFeature>,
  family: StreetSurfaceFamily,
): readonly Readonly<{
  kind: LongitudinalRoadMarkingKind;
  offsetM: number;
  widthM: number;
  style: RoadMarkingLineStyle;
}>[] {
  if (
    feature.kind !== "road" ||
    isPedestrianStreetFeature(feature) ||
    streetFeatureWidthM(feature) <
      ROAD_MARKING_RENDER_CONFIG.minimumMarkedCarriagewayWidthM ||
    !isMarkablePavedSurface(feature, family)
  ) {
    return [];
  }
  const widthM = streetFeatureWidthM(feature);
  const markings: Array<{
    kind: LongitudinalRoadMarkingKind;
    offsetM: number;
    widthM: number;
    style: RoadMarkingLineStyle;
  }> = [];
  const mapped = feature.roadEvidenceSourceId !== undefined;
  const hasRestrictedChanges = [
    ...(feature.changeLanesForward ?? []),
    ...(feature.changeLanesBackward ?? []),
  ].some((token) => token !== "yes" && token !== "none");
  const hasTurnLanes =
    (feature.turnLanesForward?.some((token) => token !== "none") ?? false) ||
    (feature.turnLanesBackward?.some((token) => token !== "none") ?? false);
  const dividerStyle: RoadMarkingLineStyle =
    hasRestrictedChanges || hasTurnLanes ? "solid" : "dashed";
  const oneway = feature.onewayDirection !== undefined;
  const halfWidthM = widthM / 2;
  const totalLanes = feature.lanes ??
    ((feature.lanesForward ?? 0) + (feature.lanesBackward ?? 0) || undefined);
  if (!oneway) {
    markings.push({
      kind: "centre",
      offsetM: 0,
      widthM: ROAD_MARKING_RENDER_CONFIG.solidLineWidthM,
      style: hasRestrictedChanges ? "solid" : "dashed",
    });
  }
  if (mapped && oneway && totalLanes !== undefined && totalLanes > 1) {
    const laneWidthM = widthM / totalLanes;
    for (let divider = 1; divider < totalLanes; divider += 1) {
      markings.push({
        kind: "lane-divider",
        offsetM: -halfWidthM + laneWidthM * divider,
        widthM: ROAD_MARKING_RENDER_CONFIG.lineWidthM,
        style: dividerStyle,
      });
    }
  } else if (mapped && !oneway) {
    const forward = feature.lanesForward ??
      (totalLanes === undefined ? 1 : Math.max(1, Math.floor(totalLanes / 2)));
    const backward = feature.lanesBackward ??
      (totalLanes === undefined ? 1 : Math.max(1, totalLanes - forward));
    const forwardLaneWidthM = halfWidthM / forward;
    const backwardLaneWidthM = halfWidthM / backward;
    for (let divider = 1; divider < forward; divider += 1) {
      markings.push({
        kind: "lane-divider",
        offsetM: forwardLaneWidthM * divider,
        widthM: ROAD_MARKING_RENDER_CONFIG.lineWidthM,
        style: dividerStyle,
      });
    }
    for (let divider = 1; divider < backward; divider += 1) {
      markings.push({
        kind: "lane-divider",
        offsetM: -backwardLaneWidthM * divider,
        widthM: ROAD_MARKING_RENDER_CONFIG.lineWidthM,
        style: dividerStyle,
      });
    }
  } else if (
    widthM >= ROAD_MARKING_RENDER_CONFIG.minimumMultiLaneCarriagewayWidthM
  ) {
    const lanesPerDirection = Math.max(
      2,
      Math.min(
        ROAD_MARKING_RENDER_CONFIG.maximumLanesPerDirection,
        Math.floor(
          widthM / (ROAD_MARKING_RENDER_CONFIG.targetLaneWidthM * 2),
        ),
      ),
    );
    const inferredLaneWidthM = halfWidthM / lanesPerDirection;
    for (let divider = 1; divider < lanesPerDirection; divider += 1) {
      const offsetM = inferredLaneWidthM * divider;
      markings.push(
        {
          kind: "lane-divider",
          offsetM,
          widthM: ROAD_MARKING_RENDER_CONFIG.lineWidthM,
          style: "dashed",
        },
        {
          kind: "lane-divider",
          offsetM: -offsetM,
          widthM: ROAD_MARKING_RENDER_CONFIG.lineWidthM,
          style: "dashed",
        },
      );
    }
  }
  if (
    mapped && ((feature.maxspeedKph ?? 0) >= 70 || (totalLanes ?? 0) >= 4)
  ) {
    const edgeOffsetM = halfWidthM - ROAD_MARKING_RENDER_CONFIG.edgeLineInsetM;
    markings.push(
      {
        kind: "edge",
        offsetM: edgeOffsetM,
        widthM: ROAD_MARKING_RENDER_CONFIG.solidLineWidthM,
        style: "solid",
      },
      {
        kind: "edge",
        offsetM: -edgeOffsetM,
        widthM: ROAD_MARKING_RENDER_CONFIG.solidLineWidthM,
        style: "solid",
      },
    );
  }
  if (feature.cycleLaneLeft === true || feature.cycleLaneRight === true) {
    const offsetM = halfWidthM - ROAD_MARKING_RENDER_CONFIG.cycleLaneInsetM;
    if (feature.cycleLaneLeft === true) {
      markings.push({
        kind: "cycle-lane",
        offsetM: -offsetM,
        widthM: ROAD_MARKING_RENDER_CONFIG.lineWidthM,
        style: "solid",
      });
    }
    if (feature.cycleLaneRight === true) {
      markings.push({
        kind: "cycle-lane",
        offsetM,
        widthM: ROAD_MARKING_RENDER_CONFIG.lineWidthM,
        style: "solid",
      });
    }
  }
  const parkingOffsetM = halfWidthM -
    ROAD_MARKING_RENDER_CONFIG.parkingLaneInsetM;
  if (feature.parkingLeft !== undefined && parkingOffsetM > 0.5) {
    markings.push({
      kind: "parking-bay",
      offsetM: -parkingOffsetM,
      widthM: ROAD_MARKING_RENDER_CONFIG.lineWidthM,
      style: feature.parkingLeft === "marked" ? "solid" : "dashed",
    });
  }
  if (feature.parkingRight !== undefined && parkingOffsetM > 0.5) {
    markings.push({
      kind: "parking-bay",
      offsetM: parkingOffsetM,
      widthM: ROAD_MARKING_RENDER_CONFIG.lineWidthM,
      style: feature.parkingRight === "marked" ? "solid" : "dashed",
    });
  }
  if (
    feature.busLanesForward?.some((token) => token === "designated") === true
  ) {
    const laneCount = Math.max(1, feature.lanesForward ?? totalLanes ?? 1);
    markings.push({
      kind: "bus-lane",
      offsetM: halfWidthM - widthM / Math.max(2, laneCount * (oneway ? 1 : 2)),
      widthM: ROAD_MARKING_RENDER_CONFIG.solidLineWidthM,
      style: "solid",
    });
  }
  if (
    feature.busLanesBackward?.some((token) => token === "designated") === true
  ) {
    const laneCount = Math.max(1, feature.lanesBackward ?? totalLanes ?? 1);
    markings.push({
      kind: "bus-lane",
      offsetM: -halfWidthM + widthM / Math.max(2, laneCount * 2),
      widthM: ROAD_MARKING_RENDER_CONFIG.solidLineWidthM,
      style: "solid",
    });
  }
  return markings;
}

function controlLineSegments(
  tile: Readonly<RuntimeWorldTilePayloadV2>,
): readonly Readonly<RoadMarkingRibbonSegment>[] {
  const roads = tile.transportLines.filter((feature) => feature.kind === "road");
  return (tile.roadControls ?? []).flatMap((control) => {
    if (control.kind !== "stop" && control.kind !== "give_way") return [];
    const road = roads.find((candidate) =>
      sourceFeatureId(candidate) === control.roadFeatureId
    );
    if (road === undefined) return [];
    const directionSign = control.directionAlongRoad === "backward" ||
        (control.directionAlongRoad === undefined &&
          road.onewayDirection === "backward")
      ? -1
      : 1;
    const travel = {
      x: control.tangent.x * directionSign,
      z: control.tangent.z * directionSign,
    };
    const right = { x: -travel.z, z: travel.x };
    const centre = {
      x: control.roadPosition.x -
        travel.x * ROAD_MARKING_RENDER_CONFIG.stopLineApproachOffsetM,
      z: control.roadPosition.z -
        travel.z * ROAD_MARKING_RENDER_CONFIG.stopLineApproachOffsetM,
    };
    const halfWidthM = streetFeatureWidthM(road) / 2;
    const from = offsetPoint(centre, right, road.onewayDirection === undefined
      ? 0.12
      : -halfWidthM + 0.25);
    const to = offsetPoint(centre, right, halfWidthM - 0.25);
    if (Math.hypot(to.x - from.x, to.z - from.z) < 0.5) return [];
    return [{
      featureId: road.id,
      kind: "stop-line",
      from,
      to,
      fromTangent: right,
      toTangent: right,
      offsetM: 0,
      widthM: ROAD_MARKING_RENDER_CONFIG.stopLineWidthM,
      style: "solid",
    }];
  });
}

function turnArrowSegments(
  tile: Readonly<RuntimeWorldTilePayloadV2>,
): readonly Readonly<RoadMarkingRibbonSegment>[] {
  return tile.transportLines.flatMap((road) => {
    if (road.kind !== "road" || road.roadEvidenceSourceId === undefined) {
      return [];
    }
    const lengthM = polylineLengthM(road.points);
    if (lengthM < ROAD_MARKING_RENDER_CONFIG.turnArrowSetbackM + 4) return [];
    const result: RoadMarkingRibbonSegment[] = [];
    if ((road.turnLanesForward?.length ?? 0) > 0) {
      const sample = samplePolylineAtDistance(
        road.points,
        lengthM - ROAD_MARKING_RENDER_CONFIG.turnArrowSetbackM,
      );
      if (sample !== null) {
        appendLaneArrows(
          result,
          road,
          road.turnLanesForward!,
          sample,
          1,
        );
      }
    }
    if ((road.turnLanesBackward?.length ?? 0) > 0) {
      const sample = samplePolylineAtDistance(
        road.points,
        ROAD_MARKING_RENDER_CONFIG.turnArrowSetbackM,
      );
      if (sample !== null) {
        appendLaneArrows(
          result,
          road,
          road.turnLanesBackward!,
          sample,
          -1,
        );
      }
    }
    return result;
  });
}

function appendLaneArrows(
  result: RoadMarkingRibbonSegment[],
  road: Readonly<RuntimeLinearFeature>,
  laneTokens: readonly string[],
  sample: Readonly<PolylineSample>,
  directionSign: -1 | 1,
): void {
  const travel = {
    x: sample.tangent.x * directionSign,
    z: sample.tangent.z * directionSign,
  };
  const right = { x: -travel.z, z: travel.x };
  const halfWidthM = streetFeatureWidthM(road) / 2;
  const laneWidthM = halfWidthM / Math.max(1, laneTokens.length);
  for (const [index, token] of laneTokens.entries()) {
    if (token === "none") continue;
    const side = directionSign > 0 ? 1 : -1;
    const laneOffsetM = side * (laneWidthM * (index + 0.5));
    const centre = offsetPoint(sample.point, right, laneOffsetM);
    const halfLengthM = ROAD_MARKING_RENDER_CONFIG.turnArrowLengthM / 2;
    const shaftFrom = offsetPoint(centre, travel, -halfLengthM);
    const shaftTo = offsetPoint(centre, travel, halfLengthM * 0.72);
    result.push(markingSegmentBetween(road.id, shaftFrom, shaftTo));
    const turnsLeft = token.includes("left") && !token.includes("right");
    const turnsRight = token.includes("right") && !token.includes("left");
    const tip = turnsLeft
      ? offsetPoint(offsetPoint(centre, travel, halfLengthM * 0.48), right, -0.82)
      : turnsRight
      ? offsetPoint(offsetPoint(centre, travel, halfLengthM * 0.48), right, 0.82)
      : offsetPoint(centre, travel, halfLengthM);
    result.push(markingSegmentBetween(road.id, shaftTo, tip));
    const wingBase = offsetPoint(tip, travel, -0.68);
    result.push(markingSegmentBetween(
      road.id,
      offsetPoint(wingBase, right, turnsLeft ? 0.38 : -0.38),
      tip,
    ));
  }
}

function markingSegmentBetween(
  featureId: string,
  from: Readonly<LocalHorizontal>,
  to: Readonly<LocalHorizontal>,
): RoadMarkingRibbonSegment {
  const dx = to.x - from.x;
  const dz = to.z - from.z;
  const lengthM = Math.max(0.001, Math.hypot(dx, dz));
  const tangent = { x: dx / lengthM, z: dz / lengthM };
  return {
    featureId,
    kind: "turn-arrow",
    from,
    to,
    fromTangent: tangent,
    toTangent: tangent,
    offsetM: 0,
    widthM: ROAD_MARKING_RENDER_CONFIG.turnArrowWidthM,
    style: "solid",
  };
}

function isMarkablePavedSurface(
  feature: Readonly<RuntimeLinearFeature>,
  family: StreetSurfaceFamily,
): boolean {
  if (feature.surfaceCode === "A" || feature.surfaceCode === "C") {
    return true;
  }
  return feature.surfaceCode === undefined && family === "general";
}

function patternedSegments(
  feature: Readonly<RuntimeLinearFeature>,
  kind: LongitudinalRoadMarkingKind,
  offsetM: number,
  widthM: number,
  style: RoadMarkingLineStyle,
  startM: number,
  endM: number,
  tileOrigin: Readonly<LocalHorizontal>,
  crossingAreas: readonly Readonly<RuntimeAreaFeature>[],
): readonly Readonly<RoadMarkingRibbonSegment>[] {
  const config = ROAD_MARKING_RENDER_CONFIG;
  const patternLengthM = style === "solid"
    ? Math.max(0.01, endM - startM)
    : config.dashLengthM + config.dashGapM;
  const firstSample = samplePolylineAtDistance(feature.points, 0);
  if (firstSample === null) {
    return [];
  }
  const worldProjectionM =
    (tileOrigin.x + firstSample.point.x) * firstSample.tangent.x +
    (tileOrigin.z + firstSample.point.z) * firstSample.tangent.z;
  const phaseM = style === "solid" ? 0 : positiveModulo(
    worldProjectionM + stableTextHash(sourceFeatureId(feature)) / 97,
    patternLengthM,
  );
  let cycleStartM = style === "solid"
    ? startM
    : startM - positiveModulo(phaseM + startM, patternLengthM);
  const result: RoadMarkingRibbonSegment[] = [];
  for (; cycleStartM < endM; cycleStartM += patternLengthM) {
    const dashStartM = Math.max(startM, cycleStartM);
    const dashEndM = Math.min(
      endM,
      cycleStartM + (style === "solid" ? patternLengthM : config.dashLengthM),
    );
    if (dashEndM - dashStartM <= 0.08) {
      continue;
    }
    const sectionCount = Math.max(
      1,
      Math.ceil(
        (dashEndM - dashStartM) / config.maximumCurveSegmentLengthM,
      ),
    );
    for (let section = 0; section < sectionCount; section += 1) {
      const fromDistanceM = dashStartM +
        (dashEndM - dashStartM) * section / sectionCount;
      const toDistanceM = dashStartM +
        (dashEndM - dashStartM) * (section + 1) / sectionCount;
      const from = samplePolylineAtDistance(feature.points, fromDistanceM);
      const to = samplePolylineAtDistance(feature.points, toDistanceM);
      if (from === null || to === null) {
        continue;
      }
      const centre = {
        x: (from.point.x + to.point.x) / 2,
        z: (from.point.z + to.point.z) / 2,
      };
      if (crossingAreas.some((area) =>
        area.polygons.some((polygon) => pointInPolygon(centre, polygon))
      )) {
        continue;
      }
      result.push({
        featureId: feature.id,
        kind,
        from: from.point,
        to: to.point,
        fromTangent: from.tangent,
        toTangent: to.tangent,
        offsetM,
        widthM,
        style,
      });
    }
  }
  return result;
}

function appendRibbonSegment(
  mesh: MutableMeshData,
  segment: Readonly<RoadMarkingRibbonSegment>,
  tile: Readonly<RuntimeWorldTilePayloadV2>,
  terrain: Readonly<TerrainTileData>,
): void {
  const feature = tile.transportLines.find(
    (candidate) => candidate.id === segment.featureId,
  );
  if (feature === undefined) {
    return;
  }
  const fromNormal = {
    x: -segment.fromTangent.z,
    z: segment.fromTangent.x,
  };
  const toNormal = {
    x: -segment.toTangent.z,
    z: segment.toTangent.x,
  };
  const halfWidthM = segment.widthM / 2;
  const points = [
    offsetPoint(segment.from, fromNormal, segment.offsetM + halfWidthM),
    offsetPoint(segment.from, fromNormal, segment.offsetM - halfWidthM),
    offsetPoint(segment.to, toNormal, segment.offsetM + halfWidthM),
    offsetPoint(segment.to, toNormal, segment.offsetM - halfWidthM),
  ];
  const heights = points.map((point) =>
    streetFeatureSurfaceElevationM(feature, terrain, tile.origin, point)
  );
  if (heights.some((height) => height === null)) {
    return;
  }
  const base = mesh.positions.length / 3;
  for (const [index, point] of points.entries()) {
    mesh.positions.push(
      point.x,
      heights[index]! + ROAD_MARKING_RENDER_CONFIG.surfaceLiftM,
      point.z,
    );
  }
  const lengthM = Math.hypot(
    segment.to.x - segment.from.x,
    segment.to.z - segment.from.z,
  );
  mesh.uvs.push(0, 0, 1, 0, 0, lengthM, 1, lengthM);
  mesh.indices.push(base, base + 2, base + 1, base + 1, base + 2, base + 3);
}

function crossingExclusionAreas(
  hardscape: readonly Readonly<RuntimeAreaFeature>[],
): readonly Readonly<RuntimeAreaFeature>[] {
  const clearanceM = ROAD_MARKING_RENDER_CONFIG.crossingClearanceM;
  return hardscape.flatMap((feature) => {
    if (!feature.classCode.toLowerCase().includes(":crossing-marking:")) {
      return [];
    }
    return [{
      ...feature,
      polygons: feature.polygons.map((polygon) => {
        const points = [polygon.outer, ...polygon.holes].flat();
        const minX = Math.min(...points.map((point) => point.x)) - clearanceM;
        const maxX = Math.max(...points.map((point) => point.x)) + clearanceM;
        const minZ = Math.min(...points.map((point) => point.z)) - clearanceM;
        const maxZ = Math.max(...points.map((point) => point.z)) + clearanceM;
        return {
          outer: [
            { x: minX, z: minZ },
            { x: maxX, z: minZ },
            { x: maxX, z: maxZ },
            { x: minX, z: maxZ },
          ],
          holes: [],
        };
      }),
    }];
  });
}

function findJunctionEndpointKeys(
  roads: readonly Readonly<RuntimeLinearFeature>[],
): ReadonlySet<string> {
  const groups = new Map<string, EndpointRecord[]>();
  for (const road of roads) {
    const start = endpointRecord(road, "start");
    const end = endpointRecord(road, "end");
    for (const record of [start, end]) {
      if (record === null) {
        continue;
      }
      const key = endpointKey(
        record.endpoint === "start" ? road.points[0] : road.points.at(-1),
      );
      if (key === null) {
        continue;
      }
      const values = groups.get(key);
      const value = {
        featureId: road.id,
        name: road.name?.trim().toLocaleLowerCase("lt-LT") ?? null,
        outward: record.outward,
      };
      if (values === undefined) {
        groups.set(key, [value]);
      } else {
        values.push(value);
      }
    }
  }
  const result = new Set<string>();
  for (const [key, records] of groups) {
    if (records.length < 2) {
      continue;
    }
    const distinctNames = new Set(records.map((record) => record.name));
    const hasBranch = records.some((left, leftIndex) =>
      records.slice(leftIndex + 1).some((right) =>
        Math.abs(
          left.outward.x * right.outward.x +
            left.outward.z * right.outward.z,
        ) < 0.92
      )
    );
    if (
      hasBranch &&
      (records.length >= 3 || distinctNames.size >= 2)
    ) {
      result.add(key);
    }
  }
  return result;
}

function endpointRecord(
  feature: Readonly<RuntimeLinearFeature>,
  endpoint: "start" | "end",
): Readonly<{
  endpoint: "start" | "end";
  outward: LocalHorizontal;
}> | null {
  const sample = endpoint === "start"
    ? firstUsableSegment(feature.points)
    : firstUsableSegment([...feature.points].reverse());
  if (sample === null) {
    return null;
  }
  return {
    endpoint,
    outward: endpoint === "start"
      ? { x: -sample.x, z: -sample.z }
      : sample,
  };
}

function firstUsableSegment(
  points: readonly Readonly<LocalHorizontal>[],
): LocalHorizontal | null {
  for (let index = 1; index < points.length; index += 1) {
    const from = points[index - 1];
    const to = points[index];
    if (from === undefined || to === undefined) {
      continue;
    }
    const deltaX = to.x - from.x;
    const deltaZ = to.z - from.z;
    const lengthM = Math.hypot(deltaX, deltaZ);
    if (lengthM > 0.02) {
      return { x: deltaX / lengthM, z: deltaZ / lengthM };
    }
  }
  return null;
}

function samplePolylineAtDistance(
  points: readonly Readonly<LocalHorizontal>[],
  requestedDistanceM: number,
): PolylineSample | null {
  const distanceM = Math.max(0, requestedDistanceM);
  let traversedM = 0;
  let fallback: PolylineSample | null = null;
  for (let index = 1; index < points.length; index += 1) {
    const from = points[index - 1];
    const to = points[index];
    if (from === undefined || to === undefined) {
      continue;
    }
    const deltaX = to.x - from.x;
    const deltaZ = to.z - from.z;
    const lengthM = Math.hypot(deltaX, deltaZ);
    if (lengthM <= 0.01) {
      continue;
    }
    const tangent = { x: deltaX / lengthM, z: deltaZ / lengthM };
    fallback = { point: { x: to.x, z: to.z }, tangent };
    if (traversedM + lengthM + 1e-6 >= distanceM) {
      const t = Math.max(
        0,
        Math.min(1, (distanceM - traversedM) / lengthM),
      );
      return {
        point: {
          x: from.x + deltaX * t,
          z: from.z + deltaZ * t,
        },
        tangent,
      };
    }
    traversedM += lengthM;
  }
  return fallback;
}

function polylineLengthM(
  points: readonly Readonly<LocalHorizontal>[],
): number {
  let lengthM = 0;
  for (let index = 1; index < points.length; index += 1) {
    const from = points[index - 1];
    const to = points[index];
    if (from !== undefined && to !== undefined) {
      lengthM += Math.hypot(to.x - from.x, to.z - from.z);
    }
  }
  return lengthM;
}

function endpointKey(
  point: Readonly<LocalHorizontal> | undefined,
): string | null {
  return point === undefined
    ? null
    : `${Math.round(point.x / ENDPOINT_QUANTIZATION_M)}:${
      Math.round(point.z / ENDPOINT_QUANTIZATION_M)
    }`;
}

function sourceFeatureId(feature: Readonly<RuntimeLinearFeature>): string {
  return feature.id.split("#", 1)[0] ?? feature.id;
}

function stableTextHash(value: string): number {
  let hash = 2_166_136_261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16_777_619);
  }
  return hash >>> 0;
}

function positiveModulo(value: number, modulus: number): number {
  return ((value % modulus) + modulus) % modulus;
}

function offsetPoint(
  point: Readonly<LocalHorizontal>,
  normal: Readonly<LocalHorizontal>,
  offsetM: number,
): LocalHorizontal {
  return {
    x: point.x + normal.x * offsetM,
    z: point.z + normal.z * offsetM,
  };
}

function countDashRuns(
  segments: readonly Readonly<RoadMarkingRibbonSegment>[],
  kind: LongitudinalRoadMarkingKind,
): number {
  let count = 0;
  let previous: Readonly<RoadMarkingRibbonSegment> | undefined;
  for (const segment of segments.filter((candidate) => candidate.kind === kind)) {
    if (
      previous === undefined ||
      previous.featureId !== segment.featureId ||
      previous.offsetM !== segment.offsetM ||
      Math.hypot(
          segment.from.x - previous.to.x,
          segment.from.z - previous.to.z,
        ) > 0.08
    ) {
      count += 1;
    }
    previous = segment;
  }
  return count;
}
